import { useEffect, useRef } from 'react'
import { GRADING } from '../constants/config'
import { STRINGS } from '../constants/strings'
import { gradeExam, saveResult } from '../data/mockApi'
import { api } from '../data/api'
import { gradeLabel, gradeShortAnswer, scoreObjective, scoreMsqCredit } from '../utils/grading'
import { useAuth, useExam } from '../hooks'

function toDisplayVerdict(v) {
  if (v === 'correct')   return 'Correct'
  if (v === 'partial')   return 'Partially Correct'
  if (v === 'incorrect') return 'Incorrect'
  return null
}

const STEPS = STRINGS.grading.steps

function buildPrepTips(weakTopics, scorePct, total, passed) {
  const status    = passed ? 'PASS ✅' : 'FAIL ❌'
  const topicsStr = weakTopics.length ? weakTopics.join(', ') : 'none identified'
  return `## Performance Summary
You scored **${scorePct.toFixed(0)}%** on this ${total}-question exam — a **${status}** relative to the ${GRADING.PASS_CUTOFF_PCT}% cutoff. ${passed ? 'You are on track!' : 'With focused revision you can reach the passing mark.'}

## Focus Areas
${weakTopics.length
  ? weakTopics.map(t => `- **${t}** — revisit core concepts and practise more questions from this area.`).join('\n')
  : '- No major weak areas detected. Keep up the good work!'}

## Study Plan
1. Re-read chapters covering: ${topicsStr}.
2. Attempt 10 targeted questions per weak topic before your next attempt.
3. Review all questions you got wrong using the answer review below.
4. Take a full exam to simulate real conditions.`
}

export default function GradingScreen() {
  const { state, dispatch } = useExam()
  const { user }            = useAuth()
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true

    if (state.mode === 'virtual' && state.attemptId) {
      const answersArr = state.questions.map((q, i) => ({
        questionId: q._id,
        chosen:     state.answers[i] ?? (q.type === 'msq' ? [] : ''),
      }))
      const timeTaken = state.startTime ? Math.round((Date.now() - state.startTime) / 1000) : null
      const flags = { autoSubmitted: state.autoSubmitted, submitReason: state.submitReason }

      api.post(`/api/users/attempts/${state.attemptId}/submit`, { answers: answersArr, timeTaken, flags }, { timeoutMs: 300_000 })
        .then(data => {
          const results = data.scored.map(s => {
            return {
              question: {
                type:            s.type,
                question:        s.question,
                options:         s.options,
                correct_answer:  s.correct_answer,
                correct_answers: s.correct_answers,
                explanation:     s.explanation,
                key_points:      s.key_points,
                topic:           s.topic,
                difficulty:      s.difficulty,
              },
              user_answer:       s.chosen ?? '',
              is_correct:        s.isCorrect,
              creditEarned:      s.creditEarned,
              marks:             s.marks,
              marksAwarded:      s.marksAwarded,
              verdict:           s.type === 'short_answer' ? toDisplayVerdict(s.aiVerdict) : null,
              keyPointsCovered:  s.keyPointsCovered ?? [],
              keyPointsMissed:   s.keyPointsMissed  ?? [],
              feedback:          s.feedback         ?? '',
            }
          })

          // Marks-weighted score — mirrors the backend's own submitAttempt calculation
          // (falls back to a plain average when every question is worth 1 mark)
          const breakdown = {}
          for (const r of results) {
            const topic = r.question.topic || 'General'
            if (!breakdown[topic]) breakdown[topic] = { correct: 0, total: 0 }
            breakdown[topic].total += 1
            breakdown[topic].correct += r.creditEarned ?? 0
          }

          const gradable          = results.filter(r => r.is_correct !== null)
          const totalMarksAwarded = gradable.reduce((sum, r) => sum + (r.marksAwarded ?? 0), 0)
          const marksPossible     = gradable.reduce((sum, r) => sum + (r.marks ?? 1), 0) || gradable.length || results.length
          const maxScore  = results.length
          const pct       = maxScore > 0 ? (totalMarksAwarded / marksPossible) * 100 : 0
          const passed    = pct >= GRADING.PASS_CUTOFF_PCT
          const score     = {
            score:             totalMarksAwarded,
            max_score:         marksPossible,
            percentage:        pct,
            grade:             gradeLabel(pct),
            passed,
            cutoff_percentage: GRADING.PASS_CUTOFF_PCT,
          }

          const weakTopics = Object.entries(breakdown)
            .filter(([, s]) => s.total > 0 && s.correct / s.total < 0.6)
            .sort(([, a], [, b]) => a.correct / a.total - b.correct / b.total)
            .map(([t]) => t)

          const prepTips = buildPrepTips(weakTopics, pct, maxScore, passed)
          dispatch({ type: 'SET_RESULTS', payload: { results, score, breakdown, weakTopics, prepTips, creditsBlockedForGrading: !!data.creditsBlockedForGrading } })
        })
        .catch(err => {
          console.error('[grading] submit failed:', err)
          // Fall back to local grading so the user still sees results
          gradeExam(state.questions, state.answers).then(payload =>
            dispatch({ type: 'SET_RESULTS', payload })
          )
        })
    } else {
      // Practice mode — grade MCQ/TF/MSQ locally, use batch AI for short answers
      ;(async () => {
        const questions = state.questions
        const answers   = state.answers
        const cachedSA  = state.shortAnswerResults  // already AI-graded via "Check Answer"

        // Resolve each question's scoring weight, same fallback chain as backend/virtual mode
        const marksFor = q => q.marks ?? 1
        let creditsBlockedForGrading = false

        // Build initial results — short answers that need batch grading are left as null
        const results = questions.map((q, i) => {
          const userAnswer = answers[i] ?? (q.type === 'msq' ? [] : '')
          const marks = marksFor(q)
          if (q.type === 'msq') {
            const creditEarned = scoreMsqCredit(userAnswer, q.correct_answers, Object.keys(q.options ?? {}).length)
            return { question: q, user_answer: userAnswer, is_correct: creditEarned === 1, creditEarned, marks,
                     verdict: null, keyPointsCovered: [], keyPointsMissed: [], feedback: '' }
          }
          if (q.type !== 'short_answer') {
            const isCorrect = scoreObjective(q, userAnswer)
            return { question: q, user_answer: userAnswer, is_correct: isCorrect, creditEarned: isCorrect ? 1 : 0, marks,
                     verdict: null, keyPointsCovered: [], keyPointsMissed: [], feedback: '' }
          }
          const cached = cachedSA[i]
          if (cached) {
            const hasMarksEarned = cached.marksEarned != null && marks > 0
            const creditEarned = hasMarksEarned ? cached.marksEarned / marks
                                : cached.verdict === 'correct' ? 1 : cached.verdict === 'partial' ? null : 0
            return { question: q, user_answer: userAnswer, marks,
                     is_correct: hasMarksEarned ? creditEarned === 1 : (cached.verdict === 'correct' ? true : cached.verdict === 'partial' ? null : false),
                     creditEarned,
                     verdict: toDisplayVerdict(cached.verdict),
                     keyPointsCovered: cached.keyPointsCovered ?? [],
                     keyPointsMissed:  cached.keyPointsMissed  ?? [],
                     feedback:         cached.feedback          ?? '' }
          }
          return null  // needs batch grading
        })

        // Batch-grade any short answers not already checked
        const uncachedIdx = results.reduce((acc, r, i) => { if (r === null) acc.push(i); return acc }, [])
        if (uncachedIdx.length > 0) {
          try {
            const batchQ = uncachedIdx.map(i => ({
              question:      questions[i].question,
              studentAnswer: answers[i] ?? '',
              correctAnswer: questions[i].correct_answer,
              keyPoints:     questions[i].key_points ?? [],
              topic:         questions[i].topic,
              marks:         marksFor(questions[i]),
            }))
            const data = await api.post('/api/ai/check-short-answers-batch', {
              questions: batchQ,
              examId: state.selectedExam?.id ?? state.selectedExam?._id,
            }, { timeoutMs: 300_000 })
            ;(data.results ?? []).forEach((r, j) => {
              const i = uncachedIdx[j]
              const marks = marksFor(questions[i])
              const hasMarksEarned = r.marksEarned != null && marks > 0
              const creditEarned = hasMarksEarned ? r.marksEarned / marks
                                  : r.verdict === 'correct' ? 1 : r.verdict === 'partial' ? null : 0
              results[i] = { question: questions[i], user_answer: answers[i] ?? '', marks,
                             is_correct: hasMarksEarned ? creditEarned === 1 : (r.verdict === 'correct' ? true : r.verdict === 'partial' ? null : false),
                             creditEarned,
                             verdict: toDisplayVerdict(r.verdict),
                             keyPointsCovered: r.keyPointsCovered ?? [],
                             keyPointsMissed:  r.keyPointsMissed  ?? [],
                             feedback:         r.feedback          ?? '' }
            })
          } catch (err) {
            if (err.code === 'INSUFFICIENT_CREDITS') {
              // A real block, not a technical failure — pretending to grade these
              // with the word-count heuristic would misrepresent a policy decision
              // (not enough balance) as if the AI had actually looked at the
              // answer. Leave them ungraded (excluded from the score, same as a
              // genuine AI failure in the real Exam path) and surface it clearly.
              creditsBlockedForGrading = true
              uncachedIdx.forEach(i => {
                const marks = marksFor(questions[i])
                results[i] = { question: questions[i], user_answer: answers[i] ?? '', marks,
                               is_correct: null, creditEarned: null,
                               verdict: null, keyPointsCovered: [], keyPointsMissed: [], feedback: '' }
              })
            } else {
              // Fallback to word-count heuristic for a genuine AI/network failure
              uncachedIdx.forEach(i => {
                const marks = marksFor(questions[i])
                const { verdict, feedback } = gradeShortAnswer(answers[i] ?? '')
                const creditEarned = verdict === 'correct' ? 1 : verdict === 'partial' ? null : 0
                results[i] = { question: questions[i], user_answer: answers[i] ?? '', marks,
                               is_correct: verdict === 'correct' ? true : verdict === 'partial' ? null : false,
                               creditEarned,
                               verdict, keyPointsCovered: [], keyPointsMissed: [], feedback }
              })
            }
          }
        }

        // Marks-weighted score — collapses to the plain average when every question is worth 1 mark
        const gradable = results.filter(r => r.creditEarned !== null)
        for (const r of gradable) r.marksAwarded = r.marks * r.creditEarned

        const breakdown = {}
        for (const r of results) {
          const topic = r.question.topic || 'General'
          if (!breakdown[topic]) breakdown[topic] = { correct: 0, total: 0 }
          breakdown[topic].total += 1
          breakdown[topic].correct += r.creditEarned ?? 0
        }

        const totalMarksAwarded = gradable.reduce((sum, r) => sum + r.marksAwarded, 0)
        const marksPossible     = gradable.reduce((sum, r) => sum + r.marks, 0) || gradable.length || results.length
        const maxScore = results.length
        const pct      = maxScore > 0 ? (totalMarksAwarded / marksPossible) * 100 : 0
        const passed   = pct >= GRADING.PASS_CUTOFF_PCT
        const score    = { score: totalMarksAwarded, max_score: marksPossible, percentage: pct,
                           grade: gradeLabel(pct), passed, cutoff_percentage: GRADING.PASS_CUTOFF_PCT }
        const weakTopics = Object.entries(breakdown)
          .filter(([, s]) => s.total > 0 && s.correct / s.total < 0.6)
          .sort(([, a], [, b]) => a.correct / a.total - b.correct / b.total)
          .map(([t]) => t)
        const prepTips = buildPrepTips(weakTopics, pct, maxScore, passed)

        if (user) {
          saveResult(user.id, { examId: state.selectedExam?.id, examName: state.selectedExam?.name,
                                mode: state.mode, difficulty: state.difficulty, score, results, breakdown, prepTips })

          const examIdForProgress = state.selectedExam?.id ?? state.selectedExam?._id
          if (examIdForProgress) {
            api.post('/api/users/trial-progress', { examId: examIdForProgress, level: state.difficulty, passed, score: pct })
              .catch(() => {}) // best-effort — never blocks the student from seeing their results
          }
        }
        dispatch({ type: 'SET_RESULTS', payload: { results, score, breakdown, weakTopics, prepTips, creditsBlockedForGrading } })
      })()
    }
  }, [])

  return (
    <div className="max-w-md mx-auto px-4 sm:px-8 py-16 sm:py-24 text-center animate-fade-up">
      {/* Spinning ring */}
      <div className="relative w-20 h-20 mx-auto mb-8">
        <div className="absolute inset-0 rounded-full border-4 border-[var(--surface-2)]" />
        <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-brand animate-spin" />
        <div className="absolute inset-2 rounded-full bg-brand/10 flex items-center justify-center text-xl">📊</div>
      </div>

      <h2 className="text-xl font-bold text-[var(--text-1)] mb-1">{STRINGS.grading.title}</h2>
      <p className="text-sm text-[var(--text-2)] mb-10">{STRINGS.grading.subtitle}</p>

      <div className="card p-4 text-left space-y-3">
        {STEPS.map((s, i) => (
          <div key={i} className="flex items-center gap-3 text-xs text-[var(--text-3)]">
            <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
              <span className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-brand animate-pulse' : 'bg-[var(--surface-2)]'}`} />
            </span>
            {s}
          </div>
        ))}
      </div>
    </div>
  )
}
